import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, MapPin, Calendar, TrendingUp, FileText, Receipt,
  ClipboardList, Download, UserPlus, Eye, CheckCircle2, Clock,
  AlertCircle, CreditCard, ChevronDown, ChevronUp, Lock, Mail, User, Phone, Printer,
  BarChart3
} from "lucide-react";
import { safeOpenHTMLWindow } from "@/utils/safeHTML";
import { downloadHtmlAsPdf } from "@/utils/htmlToPdf";
import ClientGanttChart from "@/components/client/ClientGanttChart";
import { generateDevizHTML } from "@/components/client/clientDevizHTML";
import GlassLayout from "@/components/GlassLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProgressBar({ value, className = "" }) {
  return (
    <div className={`w-full bg-slate-200 rounded-full h-2.5 ${className}`}>
      <div
        className="bg-teal-500 h-2.5 rounded-full transition-all duration-500"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: { label: "Ciornă", cls: "bg-slate-100 text-slate-700" },
    emisa: { label: "Emisă", cls: "bg-blue-100 text-blue-700" },
    platita: { label: "Plătită", cls: "bg-green-100 text-green-700" },
    anulata: { label: "Anulată", cls: "bg-red-100 text-red-700" },
  };
  const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`} data-testid={`invoice-status-${status}`}>
      {s.label}
    </span>
  );
}

export default function ClientPortal() {
  const { token: tokenParam, slug: slugParam } = useParams();
  const [resolvedToken, setResolvedToken] = useState(tokenParam || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [site, setSite] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [phases, setPhases] = useState([]);
  const [contract, setContract] = useState(null);
  const [devize, setDevize] = useState([]);
  const [invoicesData, setInvoicesData] = useState({ invoices: [], summary: {} });
  const [activeTab, setActiveTab] = useState("overview");
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [annexes, setAnnexes] = useState([]);
  const [portalDocuments, setPortalDocuments] = useState([]);

  // Create account form
  const [accName, setAccName] = useState("");
  const [accEmail, setAccEmail] = useState("");
  const [accPhone, setAccPhone] = useState("");
  const [accPassword, setAccPassword] = useState("");
  const [accLoading, setAccLoading] = useState(false);

  // Dacă intrăm pe ruta scurtă /p/:slug → rezolvăm slug-ul la token real
  useEffect(() => {
    if (tokenParam) {
      setResolvedToken(tokenParam);
      return;
    }
    if (!slugParam) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/p/resolve/${slugParam}`);
        setResolvedToken(r.data.token);
      } catch (err) {
        setError(err.response?.data?.detail || "Link invalid sau expirat");
        setLoading(false);
      }
    })();
  }, [tokenParam, slugParam]);

  const fetchData = useCallback(async () => {
    if (!resolvedToken) return;
    setLoading(true);
    try {
      const [siteRes, phasesRes, contractRes, devizRes, invoicesRes, annexesRes, docsRes] = await Promise.all([
        axios.get(`${API}/client-portal/access/${resolvedToken}`),
        axios.get(`${API}/client-portal/access/${resolvedToken}/phases`),
        axios.get(`${API}/client-portal/access/${resolvedToken}/contract`),
        axios.get(`${API}/client-portal/access/${resolvedToken}/deviz`),
        axios.get(`${API}/client-portal/access/${resolvedToken}/invoices`),
        axios.get(`${API}/client-portal/access/${resolvedToken}/annexes`).catch(() => ({ data: [] })),
        axios.get(`${API}/client-portal/access/${resolvedToken}/documents`).catch(() => ({ data: { documents: [] } })),
      ]);
      setSite(siteRes.data.site);
      setCompanyName(siteRes.data.company_name);
      setPhases(phasesRes.data || []);
      setContract(contractRes.data);
      setDevize(devizRes.data || []);
      setInvoicesData(invoicesRes.data || { invoices: [], summary: {} });
      setAnnexes(annexesRes.data || []);
      setPortalDocuments(docsRes.data?.documents || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Link invalid sau expirat");
    } finally {
      setLoading(false);
    }
  }, [resolvedToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccLoading(true);
    try {
      await axios.post(`${API}/client-portal/create-account`, {
        name: accName,
        email: accEmail,
        phone: accPhone,
        password: accPassword,
        portal_token: resolvedToken,
      });
      toast.success("Cont creat cu succes! Puteți accesa portalul cu email și parolă.");
      setShowCreateAccount(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la crearea contului");
    } finally {
      setAccLoading(false);
    }
  };

  const downloadPDF = async (htmlContent, filename) => {
    if (!htmlContent) return;
    toast.loading("Se generează PDF-ul...", { id: "pdf-dl" });
    try {
      await downloadHtmlAsPdf(htmlContent, filename);
      toast.success("PDF descărcat!", { id: "pdf-dl" });
    } catch (err) {
      toast.error("Eroare la generarea PDF", { id: "pdf-dl" });
    }
  };

  const printHTML = (htmlContent) => {
    if (!htmlContent) return;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:none";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(htmlContent);
    doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 300);
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="portal-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4" />
          <p className="text-slate-600">Se încarcă portalul...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="portal-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Acces restricționat</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Prezentare", icon: Building2 },
    { id: "phases", label: "Etape", icon: ClipboardList },
    { id: "financial", label: "Financiar", icon: Receipt },
    { id: "documents", label: "Documente", icon: FileText },
    { id: "gantt", label: "Grafic Gantt", icon: BarChart3 },
  ];

  const overallProgress = phases.length > 0
    ? phases.reduce((sum, p) => sum + (p.progress_percent || 0), 0) / phases.length
    : (site?.progress_percent || 0);

  const summary = invoicesData.summary || {};

  return (
    <GlassLayout data-testid="client-portal">
      {/* Header */}
      <header className="glass-header sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 text-base leading-tight" data-testid="portal-company-name">{companyName}</h1>
              <p className="text-xs text-slate-500">Portal Client</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCreateAccount(true)}
            className="border-teal-500 text-teal-600 hover:bg-teal-50"
            data-testid="create-account-btn"
          >
            <UserPlus className="w-4 h-4 mr-1.5" />
            Creează cont client
          </Button>
        </div>
      </header>

      {/* Project header */}
      <div className="bg-gradient-to-r from-teal-600 to-blue-700 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <h2 className="text-2xl font-bold mb-1" data-testid="portal-project-name">{site?.name}</h2>
          <div className="flex flex-wrap gap-4 text-sm text-teal-100">
            {site?.address && (
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{site.address}</span>
            )}
            {site?.start_date && (
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{site.start_date} — {site.end_date || "în curs"}</span>
            )}
          </div>
          <div className="mt-4 max-w-md">
            <div className="flex items-center justify-between text-sm mb-1">
              <span>Progres general</span>
              <span className="font-bold">{overallProgress.toFixed(0)}%</span>
            </div>
            <ProgressBar value={overallProgress} className="!h-3 !bg-teal-400/30" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 -mb-px overflow-x-auto" data-testid="portal-tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === t.id
                    ? "border-teal-500 text-teal-600"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                }`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === "overview" && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="overview-tab">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Progres</p>
                    <p className="text-xl font-bold text-slate-800" data-testid="overview-progress">{overallProgress.toFixed(0)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Etape</p>
                    <p className="text-xl font-bold text-slate-800" data-testid="overview-phases-count">{phases.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total facturat</p>
                    <p className="text-xl font-bold text-slate-800" data-testid="overview-total-invoiced">
                      {(summary.total_facturat || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Plătit</p>
                    <p className="text-xl font-bold text-slate-800" data-testid="overview-total-paid">
                      {(summary.total_platit || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phases summary */}
            {phases.length > 0 && (
              <div className="md:col-span-2 lg:col-span-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Etape proiect</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {phases.map((phase) => (
                      <div key={phase.id} className="flex items-center gap-4" data-testid={`phase-overview-${phase.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{phase.name}</p>
                          <ProgressBar value={phase.progress_percent || 0} />
                        </div>
                        <span className="text-sm font-bold text-slate-600 w-12 text-right">{(phase.progress_percent || 0).toFixed(0)}%</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {activeTab === "phases" && (
          <div className="space-y-4" data-testid="phases-tab">
            {phases.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Nu sunt etape definite încă.</p>
                </CardContent>
              </Card>
            ) : (
              phases.map((phase, idx) => (
                <Card key={phase.id} data-testid={`phase-card-${phase.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                          {phase.name}
                        </h3>
                        {phase.description && <p className="text-sm text-slate-500 mt-1">{phase.description}</p>}
                      </div>
                      <span className="text-lg font-bold text-teal-600">{(phase.progress_percent || 0).toFixed(0)}%</span>
                    </div>
                    <ProgressBar value={phase.progress_percent || 0} className="mb-3" />
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      {phase.start_date && (
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{phase.start_date} — {phase.end_date}</span>
                      )}
                      {phase.total_value > 0 && (
                        <span className="flex items-center gap-1 font-semibold text-slate-700">
                          <Receipt className="w-3.5 h-3.5 text-green-600" />
                          Valoare: {phase.total_value.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                        </span>
                      )}
                      {phase.expenses > 0 && (
                        <span className="flex items-center gap-1">
                          <CreditCard className="w-3.5 h-3.5 text-amber-600" />
                          Cheltuieli: {phase.expenses.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {activeTab === "financial" && (
          <div className="space-y-4" data-testid="financial-tab">
            {/* Valoare totală proiect */}
            {(() => {
              const projectTotalValue = site?.total_value || devize.reduce((s, d) => s + (d.totals?.totalGeneral || d.totals?.totalCuTVA || 0), 0);
              return projectTotalValue > 0 ? (
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 mb-1">Valoare totală proiect</p>
                    <p className="text-3xl font-bold text-slate-800" data-testid="fin-project-total">
                      {projectTotalValue.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                    </p>
                  </CardContent>
                </Card>
              ) : null;
            })()}

            {/* Summary cards */}
            <div className="grid gap-4 sm:grid-cols-3">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-1">TOTAL FACTURAT</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="fin-total-invoiced">
                    {(summary.total_facturat || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                  </p>
                  <p className="text-xs text-slate-400 mt-1">{summary.numar_facturi || 0} facturi</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-1">Total plătit</p>
                  <p className="text-2xl font-bold text-green-700" data-testid="fin-total-paid">
                    {(summary.total_platit || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                  </p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-1">Rest de plată</p>
                  <p className="text-2xl font-bold text-amber-700" data-testid="fin-remaining">
                    {(summary.rest_de_plata || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Progress bar — cât s-a achitat din total facturat */}
            {(summary.total_facturat || 0) > 0 && (
              <Card data-testid="fin-progress-card">
                <CardContent className="p-4">
                  <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                    <span>Progres încasare</span>
                    <span className="font-semibold text-green-700">{summary.progress_percent || 0}%</span>
                  </div>
                  <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all duration-500"
                      style={{ width: `${Math.min(summary.progress_percent || 0, 100)}%` }}
                      data-testid="fin-progress-bar"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Invoice details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Facturi detaliate</CardTitle>
              </CardHeader>
              <CardContent>
                {invoicesData.invoices.length === 0 ? (
                  <p className="text-center text-slate-500 py-6">Nu sunt facturi încă.</p>
                ) : (
                  <div className="space-y-2">
                    {invoicesData.invoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="border rounded-lg overflow-hidden"
                        data-testid={`invoice-row-${inv.id}`}
                      >
                        <button
                          className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors text-left"
                          onClick={() => setExpandedInvoice(expandedInvoice === inv.id ? null : inv.id)}
                          data-testid={`invoice-expand-${inv.id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Receipt className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-700 truncate">
                                {inv.series}{inv.number} — {inv.invoice_date}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{inv.client_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <StatusBadge status={inv.status} />
                            <span className="text-sm font-bold text-slate-700">{(inv.total || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</span>
                            {expandedInvoice === inv.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                        </button>
                        {expandedInvoice === inv.id && (
                          <div className="border-t bg-slate-50 p-3" data-testid={`invoice-details-${inv.id}`}>
                            {inv.source === "external" ? (
                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 py-2">
                                <div className="text-xs text-slate-600">
                                  <div className="font-semibold text-slate-700 mb-0.5">Factură externă (PDF încărcat)</div>
                                  {inv.notes && <div className="text-slate-500">{inv.notes}</div>}
                                </div>
                                <a
                                  href={`${API}/portal/invoices/${inv.id}/external-pdf?portal_token=${resolvedToken}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium"
                                  data-testid={`download-external-invoice-${inv.id}`}
                                >
                                  <Download className="w-3.5 h-3.5" /> Descarcă PDF
                                </a>
                              </div>
                            ) : (
                              <>
                              <table className="w-full text-xs">
                              <thead>
                                <tr className="text-slate-500">
                                  <th className="text-left pb-1.5">Articol</th>
                                  <th className="text-right pb-1.5">Cant.</th>
                                  <th className="text-right pb-1.5">Preț unit.</th>
                                  <th className="text-right pb-1.5">TVA</th>
                                  <th className="text-right pb-1.5">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(inv.items || []).map((item, i) => (
                                  <tr key={`${item.name}-${i}`} className="border-t border-slate-200">
                                    <td className="py-1 text-slate-700">{item.name}</td>
                                    <td className="py-1 text-right text-slate-600">{item.quantity} {item.um}</td>
                                    <td className="py-1 text-right text-slate-600">{(item.unit_price || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="py-1 text-right text-slate-600">{item.vat_percent || 21}%</td>
                                    <td className="py-1 text-right font-medium text-slate-700">{(item.total || item.quantity * item.unit_price || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="flex justify-end gap-6 mt-2 pt-2 border-t border-slate-200 text-xs">
                              <span className="text-slate-500">Subtotal: <b className="text-slate-700">{(inv.subtotal || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</b></span>
                              <span className="text-slate-500">TVA: <b className="text-slate-700">{(inv.vat_amount || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</b></span>
                              <span className="text-slate-500">Total: <b className="text-slate-800">{(inv.total || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</b></span>
                            </div>
                            </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "documents" && (
          <div className="space-y-4" data-testid="documents-tab">
            {/* Documente trimise prin portal (contract + anexe + deviz din email cu link) */}
            {portalDocuments.length > 0 && (
              <Card className="border-teal-200 bg-teal-50/40" data-testid="portal-documents-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-teal-700 flex items-center gap-2">
                    <Download className="w-5 h-5" />
                    Documente noi de descărcat ({portalDocuments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-2">
                  {portalDocuments.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-teal-100 hover:shadow-sm transition-shadow" data-testid={`portal-doc-${doc.id}`}>
                      {doc.has_thumbnail ? (
                        <img
                          src={`${API}/client-portal/access/${resolvedToken}/documents/${doc.id}/thumbnail`}
                          alt="Preview"
                          className="w-16 h-20 object-cover rounded border border-slate-200 shadow-sm flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-16 h-20 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 text-sm truncate">{doc.filename}</div>
                        <div className="text-xs text-slate-500 mt-1 flex gap-3 flex-wrap">
                          <span className="capitalize">{doc.doc_type === "annex" ? "Anexă" : doc.doc_type}</span>
                          <span>· {(doc.size / 1024).toFixed(0)} KB</span>
                          {doc.uploaded_at && <span>· {new Date(doc.uploaded_at).toLocaleDateString("ro-RO")}</span>}
                        </div>
                      </div>
                      <a
                        href={`${API}/client-portal/access/${resolvedToken}/documents/${doc.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium transition-colors flex-shrink-0"
                        data-testid={`download-portal-doc-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                        Descarcă
                      </a>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Contract */}
            {contract && (
              <Card data-testid="portal-contract-card">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800">{contract.title || "Contract servicii"}</h3>
                    <p className="text-sm text-slate-500">Nr. {contract.registration_number} — {contract.document_type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {contract.html_content && (
                      <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          safeOpenHTMLWindow(contract.html_content);
                        }}
                        data-testid="view-contract-btn"
                      >
                        <Eye className="w-4 h-4 mr-1" /> Vizualizează
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => printHTML(contract.html_content)}
                        className="text-slate-600"
                        data-testid="print-contract-btn"
                      >
                        <Printer className="w-4 h-4 mr-1" /> Print
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadPDF(contract.html_content, `Contract ${contract.registration_number || ""}`)}
                        className="text-teal-600 border-teal-300"
                        data-testid="download-contract-btn"
                      >
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Devize */}
            {devize.length > 0 && devize.map((d) => {
              const devizHtml = generateDevizHTML(d);
              return (
                <Card key={d.id} data-testid={`portal-deviz-card-${d.id}`}>
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="w-6 h-6 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800">Deviz #{d.numarDeviz || d.numar_deviz || "—"}</h3>
                      <p className="text-sm text-slate-500">{d.obiectiv || d.beneficiar || "—"}</p>
                    </div>
                    <div className="text-right mr-4">
                      <p className="font-bold text-slate-700">
                        {(d.totals?.totalGeneral || d.totals?.totalCuTVA || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                      </p>
                      <p className="text-xs text-slate-500">cu TVA</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => safeOpenHTMLWindow(devizHtml)} data-testid={`view-deviz-${d.id}`}>
                        <Eye className="w-4 h-4 mr-1" /> Vizualizează
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => printHTML(devizHtml)} className="text-slate-600" data-testid={`print-deviz-${d.id}`}>
                        <Printer className="w-4 h-4 mr-1" /> Print
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadPDF(devizHtml, `Deviz ${d.numarDeviz || d.numar_deviz || ""}`)} className="text-teal-600 border-teal-300" data-testid={`download-deviz-${d.id}`}>
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {!contract && devize.length === 0 && annexes.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Nu sunt documente disponibile.</p>
                </CardContent>
              </Card>
            )}

            {/* Anexe Contract */}
            {annexes.length > 0 && (
              <Card data-testid="portal-annexes-card">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-violet-600" />
                    Anexe Contract
                  </h3>
                  <div className="space-y-2">
                    {annexes.map((annex) => (
                      <div key={annex.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid={`portal-annex-${annex.id}`}>
                        <div>
                          <p className="font-medium text-slate-700">{annex.annex_label} — {annex.title}</p>
                          <p className="text-xs text-slate-500">{new Date(annex.created_at).toLocaleDateString("ro-RO")}</p>
                        </div>
                        {annex.html_content && (
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" onClick={() => { safeOpenHTMLWindow(annex.html_content); }} data-testid={`view-annex-${annex.id}`}>
                              <Eye className="w-4 h-4 mr-1" /> Vizualizează
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => printHTML(annex.html_content)} className="text-slate-600" data-testid={`print-annex-${annex.id}`}>
                              <Printer className="w-4 h-4 mr-1" /> Print
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => downloadPDF(annex.html_content, `${annex.annex_label} - ${annex.title}`)} className="text-teal-600 border-teal-300" data-testid={`download-annex-${annex.id}`}>
                              <Download className="w-4 h-4 mr-1" /> PDF
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Facturi */}
            {invoicesData.invoices.length > 0 && (
              <Card data-testid="portal-invoices-docs-card">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-blue-600" />
                    Facturi ({invoicesData.invoices.length})
                  </h3>
                  <div className="space-y-2">
                    {invoicesData.invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid={`portal-doc-invoice-${inv.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Receipt className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-700">Factură {inv.series}{inv.number || inv.invoice_number}</p>
                            <p className="text-xs text-slate-500">{inv.invoice_date} — {inv.client_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${inv.status === "platita" ? "bg-green-100 text-green-700" : inv.status === "emisa" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                            {inv.status === "platita" ? "Plătită" : inv.status === "emisa" ? "Emisă" : "Ciornă"}
                          </span>
                          <p className="font-bold text-slate-700">{(inv.total || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</p>
                          {inv.source === "external" && (
                            <a
                              href={`${API}/portal/invoices/${inv.id}/external-pdf?portal_token=${resolvedToken}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium flex-shrink-0"
                              data-testid={`doc-download-external-invoice-${inv.id}`}
                            >
                              <Download className="w-3.5 h-3.5" /> Descarcă
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === "gantt" && (
          <div data-testid="gantt-tab">
            <ClientGanttChart site={site} phases={phases} />
          </div>
        )}
      </div>

      {/* Create account dialog */}
      <Dialog open={showCreateAccount} onOpenChange={setShowCreateAccount}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-teal-600" />
              Creează cont client
            </DialogTitle>
            <DialogDescription>
              Creați un cont pentru a accesa portalul oricând cu email și parolă.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAccount} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="acc-name">Nume</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="acc-name" value={accName} onChange={(e) => setAccName(e.target.value)} required placeholder="Nume complet" className="pl-10" data-testid="acc-name-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="acc-email" type="email" value={accEmail} onChange={(e) => setAccEmail(e.target.value)} required placeholder="email@exemplu.ro" className="pl-10" data-testid="acc-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-phone">Telefon</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="acc-phone" type="tel" value={accPhone} onChange={(e) => setAccPhone(e.target.value)} placeholder="07XX XXX XXX" className="pl-10" data-testid="acc-phone-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-password">Parolă</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="acc-password" type="password" value={accPassword} onChange={(e) => setAccPassword(e.target.value)} required minLength={6} placeholder="Minim 6 caractere" className="pl-10" data-testid="acc-password-input" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700" disabled={accLoading} data-testid="create-account-submit">
              {accLoading ? "Se procesează..." : "Creează cont"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </GlassLayout>
  );
}
